import { useState } from 'react';
import { useStore } from '../store';
import { getDocumentTypeById, getPageFormatById, getCategoryById } from '../data/document-presets';
import {
  FileText,
  ChevronDown,
  Settings,
  BookOpen,
  Briefcase,
  Scale,
  Sparkles,
  Megaphone,
  LucideIcon,
} from 'lucide-react';

// Category icon mapping
const categoryIcons: Record<string, LucideIcon> = {
  books: BookOpen,
  business: Briefcase,
  legal: Scale,
  creative: Sparkles,
  marketing: Megaphone,
};

interface DocumentTypeIndicatorProps {
  onOpenWizard?: () => void;
}

export default function DocumentTypeIndicator({
  onOpenWizard,
}: DocumentTypeIndicatorProps) {
  const { documentConfig } = useStore();
  const [showDropdown, setShowDropdown] = useState(false);

  if (!documentConfig) {
    return (
      <button
        onClick={onOpenWizard}
        className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
      >
        <FileText size={14} />
        <span>No document type</span>
      </button>
    );
  }

  const docType = getDocumentTypeById(documentConfig.documentType);
  const pageFormat = getPageFormatById(documentConfig.pageFormat);
  const category = getCategoryById(documentConfig.category);

  const CategoryIcon = category ? categoryIcons[category.id] || FileText : FileText;

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        style={{ borderColor: category?.color ? `${category.color}40` : undefined }}
      >
        <CategoryIcon
          size={14}
          className="flex-shrink-0"
          style={{ color: category?.color }}
        />
        <span className="font-medium text-gray-700">{docType?.name || 'Document'}</span>
        <span className="text-gray-400">|</span>
        <span className="text-gray-500">{pageFormat?.shortName || 'Unknown'}</span>
        <ChevronDown
          size={14}
          className={`text-gray-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowDropdown(false)}
          />
          <div className="absolute top-full mt-1 right-0 z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[240px]">
            {/* Document Info */}
            <div className="px-4 py-2 border-b border-gray-100">
              <div className="flex items-center gap-2 mb-2">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${category?.color}20`, color: category?.color }}
                >
                  <CategoryIcon size={16} />
                </div>
                <div>
                  <p className="font-medium text-gray-900 text-sm">{docType?.name}</p>
                  <p className="text-xs text-gray-500">{category?.name}</p>
                </div>
              </div>
            </div>

            {/* Format Info */}
            <div className="px-4 py-2 border-b border-gray-100">
              <p className="text-xs text-gray-500 mb-1">Page Format</p>
              <p className="text-sm font-medium text-gray-900">{pageFormat?.name}</p>
              <p className="text-xs text-gray-500">
                {pageFormat?.widthInches}" x {pageFormat?.heightInches}" ({pageFormat?.orientation})
              </p>
            </div>

            {/* Features */}
            {docType && (
              <div className="px-4 py-2 border-b border-gray-100">
                <p className="text-xs text-gray-500 mb-2">Features</p>
                <div className="flex flex-wrap gap-1">
                  {docType.features.showImages && (
                    <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded">
                      Images
                    </span>
                  )}
                  {docType.features.showCover && (
                    <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                      Cover
                    </span>
                  )}
                  {docType.features.showChapters && (
                    <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                      Chapters
                    </span>
                  )}
                  {docType.features.showPageNumbers && (
                    <span className="px-1.5 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                      Page #
                    </span>
                  )}
                  {docType.features.showHeaders && (
                    <span className="px-1.5 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                      Headers
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="px-2 py-1">
              <button
                onClick={() => {
                  setShowDropdown(false);
                  onOpenWizard?.();
                }}
                className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded transition-colors"
              >
                <Settings size={14} />
                Change document type
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
